/**
 * Normalizes a single service charge item to standardized display format
 * Maps raw API/mock fields to component display properties with fallbacks
 * @param {Object} item - Raw service charge object (from API or mock data)
 * @returns {Object} Normalized item with properties: id, service, sku, ref, txn, unit, qty, rate, add, amount
 */
export const normalizeServiceChargeItem = item => ({
    id: item.id,
    service: item.description || item.services || item.serviceType || 'Unknown Service',
    sku: item.productSKU || item.productSku || item.ProductSKU || 'N/A',
    ref: item.referenceNumber || 'N/A',
    txn: item.transactionType || 'Unknown',
    unit: item.unitType || 'Per Unit',
    qty: item.quantity || 0,
    rate: item.rate === undefined ? 0 : item.rate,
    add: item.additionalCharge || 0,
    amount: item.amount || 0,
});

/**
 * Transforms API response data into serviceCharges format
 * Maps API fields to component expected structure with fallbacks
 * @param {Object} apiData - API response object with EntityList array
 * @returns {Array} Transformed service charges array
 */
export const transformAPIDataToServiceCharges = apiData => {
    if (!apiData || !Array.isArray(apiData.EntityList)) {
        return [];
    }

    return apiData.EntityList.map(normalizeServiceChargeItem);
};

/**
 * Transforms mock service charges using unified normalizer
 * Ensures mock data follows same transformation as API data
 * @param {Array} mockData - Array of raw service charge objects
 * @returns {Array} Transformed service charges array
 */
export const transformMockServiceCharges = mockData => {
    if (!Array.isArray(mockData)) {
        return [];
    }

    return mockData.map(normalizeServiceChargeItem);
};

/**
 * Calculates invoice summary (subtotal, additional charges, tax, total due)
 * @param {Array} serviceCharges - Array of service charge items
 * @param {Number} taxRate - Tax rate percentage (default: 0)
 * @returns {Object} Summary object with subtotal, totalAdditionalCharges, tax, totalDue
 */
export const calculateInvoiceSummary = (serviceCharges, taxRate = 0) => {
    const subtotal = serviceCharges.reduce((sum, item) => sum + (item.amount || 0), 0);
    const totalAdditionalCharges = serviceCharges.reduce((sum, item) => sum + (item.add || 0), 0);
    const tax = (subtotal * taxRate) / 100;
    const totalDue = subtotal + totalAdditionalCharges + tax;

    return { subtotal, totalAdditionalCharges, tax, totalDue };
};

/**
 * Parses combined company/seller string into separate values
 * Format: "Company Name/Seller Name" → { company: "Company Name", seller: "Seller Name" }
 * Fallback: If no "/" found, entire string becomes company, seller is empty
 * @param {String} companyName - Combined company/seller string
 * @returns {Object} { company: string, seller: string } with trimmed whitespace
 */
export const parseCompanyAndSeller = companyName => {
    if (!companyName) {
        return { company: '', seller: '' };
    }

    if (companyName.includes('/')) {
        const [company, seller] = companyName.split('/');
        return {
            company: company.trim(),
            seller: seller.trim(),
        };
    }

    return { company: companyName.trim(), seller: '' };
};

/**
 * Extracts the earliest createdDate from service charges API response
 * Finds minimum date from EntityList array to determine billing period start
 * @param {Object} apiData - API response object with EntityList array
 * @returns {String} Earliest date in YYYY-MM-DD format, or empty string if no data
 */
export const getEarliestCreatedDate = apiData => {
    if (!apiData || !Array.isArray(apiData.EntityList) || apiData.EntityList.length === 0) {
        return '';
    }

    try {
        // Extract and parse dates from createdDate field (format: "2026-06-01T00:00:00")
        const dates = apiData.EntityList
            .map(item => item.createdDate)
            .filter(Boolean)
            .map(date => new Date(date.split('T')[0]));

        if (dates.length === 0) return '';

        // Find minimum date
        const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
        const year = minDate.getFullYear();
        const month = String(minDate.getMonth() + 1).padStart(2, '0');
        const day = String(minDate.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
    } catch (error) {
        console.error('Error extracting earliest created date:', error);
        return '';
    }
};

/**
 * Calculates the Period To date based on billing cycle type
 * Format: YYYY-MM-DD input and output
 * Monthly (hasRentItems=true): Adds 30 days
 * Daily (hasRentItems=false): Adds 1 day
 * @param {String} fromDateStr - Start date in YYYY-MM-DD format
 * @param {Boolean} hasRentItems - Billing cycle flag (true=Monthly, false=Daily)
 * @returns {String} Calculated end date in YYYY-MM-DD format, or empty string if invalid
 */
export const calculatePeriodToDate = (fromDateStr, hasRentItems) => {
    if (!fromDateStr) {
        return '';
    }

    try {
        const fromDate = new Date(fromDateStr);
        if (Number.isNaN(fromDate.getTime())) {
            return '';
        }

        const daysToAdd = hasRentItems ? 30 : 1;
        const toDate = new Date(fromDate);
        toDate.setDate(toDate.getDate() + daysToAdd);

        const year = toDate.getFullYear();
        const month = String(toDate.getMonth() + 1).padStart(2, '0');
        const day = String(toDate.getDate()).padStart(2, '0');
        
        return `${year}-${month}-${day}`;
    } catch (error) {
        console.error('Error calculating period to date:', error);
        return '';
    }
};
